import { AccountTokenStatus, AccountTokenType } from "@eisox/api/core-api";
import { toast } from "@eisox/design-system";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { accountApi } from "~/api";
import { m } from "~/paraglide/messages";

/**
 * Guards the two token-bearing routes: the form is only worth rendering for a live token of the
 * expected family. Anything else redirects to the screen that explains why, and throws — so callers
 * can treat a return as "the token is usable".
 */
export const assertAccountTokenUsable = async (
  queryClient: QueryClient,
  token: string,
  expected: AccountTokenType,
): Promise<void> => {
  const { status, type } = await queryClient.ensureQueryData(accountApi.queries.tokenStatus(token));

  if (status === AccountTokenStatus.EXPIRED) throw redirect({ to: "/expired-token", search: { token } });
  if (status === AccountTokenStatus.ALREADY_USED) throw redirect({ to: "/used-token", search: { token } });

  if (status !== AccountTokenStatus.VALID || type !== expected) {
    toast.error(m.token_invalid());
    throw redirect({ to: "/sign-in", search: { redirect: "" } });
  }
};

/**
 * Loader shared by the expired/used token screens: the family picks the wording, the address is what
 * a new link would be sent to. Without either there is nothing to show, so fall back to sign-in.
 */
export const loadInvalidTokenContext = async (
  queryClient: QueryClient,
  token: string,
): Promise<{ type: AccountTokenType; email: string }> => {
  const { type, email } = await queryClient.ensureQueryData(accountApi.queries.tokenStatus(token));
  if (!type || !email) throw redirect({ to: "/sign-in", search: { redirect: "" } });
  return { type, email };
};
